document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.gb-header-wrapper').forEach((wrapper) => {
    const isTransparent = wrapper.dataset.transparent === 'true';
    const isStickyEnabled = wrapper.dataset.sticky === 'true';
    const headerBar = wrapper.querySelector('[data-gb-header-bar]');
    const drawer = wrapper.querySelector('[data-drawer]');
    const overlay = wrapper.querySelector('[data-drawer-overlay]');
    const searchBar = wrapper.querySelector('[data-desktop-search]');

    // ==========================================
    // KEEP THE FIXED HEADER INSIDE THE VISIBLE PAGE AREA
    // ==========================================
    // Horizon may use a full-viewport cart-drawer host with a smaller dialog
    // panel inside it. Measure the visible panel, not just the outer host.
    const nativeCartDrawer = document.querySelector('#cart-drawer');

    const getVisibleCartDrawerPanel = () => {
      if (!nativeCartDrawer) return null;
      const selectors = [
        'dialog[open]',
        '.cart-drawer__dialog',
        '.cart-drawer__panel',
        '.cart-drawer__inner',
        '[role="dialog"]'
      ];
      const candidates = [];
      selectors.forEach((selector) => {
        nativeCartDrawer.querySelectorAll(selector).forEach((el) => {
          if (!candidates.includes(el)) candidates.push(el);
        });
      });
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const visiblePanels = candidates.map((el) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return {
          el,
          rect,
          visible: rect.width > 0 && rect.height > 0 && style.display !== 'none' &&
            style.visibility !== 'hidden' && Number(style.opacity || 1) > 0
        };
      }).filter((item) => item.visible);
      if (visiblePanels.length) {
        const rightEdgePanels = visiblePanels.filter((item) =>
          item.rect.right >= viewportWidth - 3 && item.rect.left > 1
        );
        const pool = rightEdgePanels.length ? rightEdgePanels : visiblePanels;
        pool.sort((a, b) => b.rect.left - a.rect.left);
        return pool[0].el;
      }
      return nativeCartDrawer;
    };

    const syncHeaderBounds = () => {
      if (!headerBar) return;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      let availableWidth = viewportWidth;
      const drawerPanel = getVisibleCartDrawerPanel();
      if (drawerPanel) {
        const rect = drawerPanel.getBoundingClientRect();
        const style = window.getComputedStyle(drawerPanel);
        const visible = rect.width > 0 && rect.height > 0 && style.display !== 'none' &&
          style.visibility !== 'hidden' && Number(style.opacity || 1) > 0;
        const isRightSidePanel = visible && rect.left > 1 &&
          rect.left < viewportWidth - 1 && rect.right >= viewportWidth - 3;
        if (isRightSidePanel) {
          availableWidth = Math.max(0, Math.min(viewportWidth, rect.left));
        }
      }
      wrapper.style.setProperty('--gb-fixed-left', '0px');
      wrapper.style.setProperty('--gb-fixed-width', `${availableWidth}px`);
      wrapper.style.width = '100%';
      wrapper.style.maxWidth = '100%';
      const sectionEl = wrapper.closest('.shopify-section');
      if (sectionEl) {
        sectionEl.style.setProperty('--gb-fixed-left', '0px');
        sectionEl.style.setProperty('--gb-fixed-width', `${availableWidth}px`);
      }
      headerBar.style.setProperty('--gb-header-visible-width', `${availableWidth}px`);
      headerBar.style.width = `${availableWidth}px`;
      headerBar.style.maxWidth = `${availableWidth}px`;
      if (!isTransparent && !wrapper.classList.contains('is-fixed')) {
        wrapper.style.height = `${headerBar.offsetHeight}px`;
      }
    };

    let headerResizeObserver;
    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(() => syncHeaderBounds());
      headerResizeObserver.observe(headerBar);
      if (nativeCartDrawer) headerResizeObserver.observe(nativeCartDrawer);
    }
    let drawerStateObserver;
    if (nativeCartDrawer && 'MutationObserver' in window) {
      drawerStateObserver = new MutationObserver(() => window.requestAnimationFrame(syncHeaderBounds));
      drawerStateObserver.observe(nativeCartDrawer, {
        attributes: true,
        attributeFilter: ['open', 'aria-hidden', 'class', 'style'],
        childList: true,
        subtree: true
      });
    }
    window.addEventListener('resize', syncHeaderBounds, { passive: true });
    window.addEventListener('orientationchange', syncHeaderBounds, { passive: true });
    ['theme-drawer:open', 'theme-drawer:close', 'cart:updated', 'cart:change', 'cart:refresh'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        window.requestAnimationFrame(syncHeaderBounds);
        window.setTimeout(syncHeaderBounds, 120);
        window.setTimeout(syncHeaderBounds, 350);
      });
    });
    syncHeaderBounds();

    // ==========================================
    // SCROLL / STICKY LOGIC
    // ==========================================
    const getScrollY = (evtTarget) => {
      if (
        evtTarget &&
        evtTarget !== document &&
        evtTarget !== window &&
        typeof evtTarget.scrollTop === 'number' &&
        !wrapper.contains(evtTarget)
      ) {
        return evtTarget.scrollTop;
      }
      return Math.max(
        window.pageYOffset || 0,
        window.scrollY || 0,
        document.scrollingElement ? document.scrollingElement.scrollTop : 0,
        document.documentElement ? document.documentElement.scrollTop : 0,
        document.body ? document.body.scrollTop : 0
      );
    };

    let lastScrollY = getScrollY();
    let ticking = false;

    const isMenuOrSearchActive = () => {
      const isDrawerOpen = drawer?.classList.contains('is-open');
      const isDesktopSearchOpen = searchBar && !searchBar.hidden;
      const isLiveResultsOpen = Array.from(wrapper.querySelectorAll('[data-live-search-results]')).some(el => !el.hidden);
      return isDrawerOpen || isDesktopSearchOpen || isLiveResultsOpen;
    };

    const updateHeaderOnScroll = (currentScroll) => {
      if (!isStickyEnabled) return; 

      const delta = currentScroll - lastScrollY;
      wrapper.classList.toggle('is-scrolled', currentScroll > 30);
      
      const shouldFix = currentScroll > 40;
      if (shouldFix && !wrapper.classList.contains('is-fixed')) syncHeaderBounds();
      wrapper.classList.toggle('is-fixed', shouldFix);

      if (currentScroll <= 60 || isMenuOrSearchActive()) {
        wrapper.classList.remove('gb-is-hidden');
      } else if (delta > 4 && currentScroll > 110) {
        wrapper.classList.add('gb-is-hidden');
      } else if (delta < -4) {
        wrapper.classList.remove('gb-is-hidden');
      }
      lastScrollY = currentScroll <= 0 ? 0 : currentScroll;
    };

    const onScrollEvent = (e) => {
      const currentScroll = getScrollY(e?.target);
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateHeaderOnScroll(currentScroll);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScrollEvent, { passive: true });
    document.addEventListener('scroll', onScrollEvent, { capture: true, passive: true });
    updateHeaderOnScroll(getScrollY());

    // ==========================================
    // DRAWER LOGIC
    // ==========================================
    const toggleDrawer = (open) => {
      drawer?.classList.toggle('is-open', open);
      overlay?.classList.toggle('is-open', open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) wrapper.classList.remove('gb-is-hidden');
      syncHeaderBounds();
    };

    wrapper.querySelector('[data-drawer-open]')?.addEventListener('click', () => toggleDrawer(true));
    wrapper.querySelector('[data-drawer-close]')?.addEventListener('click', () => toggleDrawer(false));
    overlay?.addEventListener('click', () => toggleDrawer(false));

    wrapper.querySelectorAll('[data-accordion-trigger]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-accordion-item]');
        if (item) btn.setAttribute('aria-expanded', String(item.classList.toggle('is-open')));
      });
    });

    // ==========================================
    // SEARCH TOGGLES
    // ==========================================
    const mobileSearchToggle = wrapper.querySelector('[data-mobile-search-toggle]');
    const mobileSearchRow = wrapper.querySelector('.gb-mobile-header__search-row');
    mobileSearchToggle?.addEventListener('click', () => {
      if (!mobileSearchRow) return;
      const willOpen = mobileSearchRow.hidden;
      mobileSearchRow.hidden = !willOpen;
      mobileSearchToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) mobileSearchRow.querySelector('input')?.focus({ preventScroll: true });
    });

    const searchToggle = wrapper.querySelector('[data-search-toggle]');
    const searchClose = wrapper.querySelector('[data-search-close]');
    searchToggle?.addEventListener('click', () => {
      searchBar.hidden = !searchBar.hidden;
      if (!searchBar.hidden) searchBar.querySelector('input')?.focus();
    });
    searchClose?.addEventListener('click', () => searchBar.hidden = true);

    // ==========================================
    // LIVE PREDICTIVE SEARCH
    // ==========================================
    const formatPrice = (rawPrice) => {
      if (rawPrice === undefined || rawPrice === null || rawPrice === '') return '';
      let num = typeof rawPrice === 'string' && rawPrice.includes('.') ? parseFloat(rawPrice) : Number(rawPrice);
      if (typeof rawPrice === 'number') { num = num / 100; }
      if (isNaN(num)) return rawPrice;
      const amount = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const amountNoDecimals = Math.round(num).toLocaleString('en-US');
      const amountComma = amount.replace(/\./g, ',');
      const format = wrapper.dataset.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals.replace(/,/g, '.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    };

    const predictiveUrl = (window.routes?.predictive_search_url || '/search/suggest') + '.json';

    wrapper.querySelectorAll('[data-live-search-wrap]').forEach((wrap) => {
      const input = wrap.querySelector('[data-live-search-input]');
      const resultsBox = wrap.querySelector('[data-live-search-results]');
      if (!input || !resultsBox) return;

      let debounceTimer = null;
      let activeController = null;

      const renderResults = (query, resources) => {
        const products = resources?.results?.products || [];
        const collections = resources?.results?.collections || [];
        const queries = resources?.results?.queries || [];

        if (!products.length && !collections.length && !queries.length) {
          resultsBox.innerHTML = `<div class="gb-ls-status">No results found for "${query}"</div>`;
          resultsBox.hidden = false;
          return;
        }

        let html = '';

        if (queries.length || collections.length) {
          html += `<div class="gb-ls-section"><div class="gb-ls-heading">Suggestions & Collections</div><div class="gb-ls-tags">`;
          queries.slice(0, 4).forEach((q) => {
            html += `<a href="${q.url}" class="gb-ls-tag">${q.styled_text || q.text}</a>`;
          });
          collections.slice(0, 4).forEach((c) => {
            html += `<a href="${c.url}" class="gb-ls-tag">${c.title}</a>`;
          });
          html += `</div></div>`;
        }

        if (products.length) {
          html += `<div class="gb-ls-section"><div class="gb-ls-heading">Products</div><div class="gb-ls-grid">`;
          products.forEach((p) => {
            const imgUrl = p.featured_image?.url || p.image || '';
            const priceDisplay = formatPrice(p.price);
            html += `
              <a href="${p.url}" class="gb-ls-item">
                <div class="gb-ls-thumb">
                  ${imgUrl ? `<img src="${imgUrl}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
                </div>
                <div class="gb-ls-info">
                  <span class="gb-ls-title">${p.title}</span>
                  ${priceDisplay ? `<span class="gb-ls-price">${priceDisplay}</span>` : ''}
                </div>
              </a>
            `;
          });
          html += `</div></div>`;
        }

        const searchAction = wrap.querySelector('form')?.getAttribute('action') || '/search';
        html += `
          <div class="gb-ls-view-all">
            <a href="${searchAction}?q=${encodeURIComponent(query)}" class="gb-outline-btn">
              VIEW ALL RESULTS FOR "${query}"
            </a>
          </div>
        `;

        resultsBox.innerHTML = html;
        resultsBox.hidden = false;
      };

      input.addEventListener('input', () => {
        const query = input.value.trim();
        clearTimeout(debounceTimer);

        if (query.length < 2) {
          if (activeController) activeController.abort();
          resultsBox.hidden = true;
          resultsBox.innerHTML = '';
          return;
        }

        debounceTimer = setTimeout(async () => {
          if (activeController) activeController.abort();
          activeController = new AbortController();

          resultsBox.innerHTML = `<div class="gb-ls-status">Searching...</div>`;
          resultsBox.hidden = false;

          try {
            const params = new URLSearchParams({
              q: query,
              'resources[type]': 'product,collection,query',
              'resources[limit]': '6',
              'resources[options][unavailable_products]': 'last'
            });

            const res = await fetch(`${predictiveUrl}?${params.toString()}`, {
              signal: activeController.signal
            });

            if (!res.ok) throw new Error('Predictive search failed');
            const data = await res.json();
            renderResults(query, data.resources);
          } catch (err) {
            if (err.name !== 'AbortError') {
              resultsBox.hidden = true;
            }
          }
        }, 250);
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && resultsBox.innerHTML.trim() !== '') {
          resultsBox.hidden = false;
        }
      });
    });

    document.addEventListener('click', (e) => {
      wrapper.querySelectorAll('[data-live-search-wrap]').forEach((wrap) => {
        if (!wrap.contains(e.target)) {
          const resultsBox = wrap.querySelector('[data-live-search-results]');
          if (resultsBox) resultsBox.hidden = true;
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        wrapper.querySelectorAll('[data-live-search-results]').forEach((box) => {
          box.hidden = true;
        });
      }
    });

  }); // End of Header Wrapper loop

  // ==========================================
  // BULLETPROOF NATIVE CART COUNTER UPDATER
  // ==========================================
  const updateCartUINodes = (count) => {
    // 1. Update the Native Theme <cart-icon> elements
    document.querySelectorAll('cart-icon').forEach(icon => {
      const parsedCount = parseInt(count) || 0;
      
      if (parsedCount > 0) {
        icon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        icon.classList.remove('header-actions__cart-icon--has-cart');
      }
      
      const bubbleTexts = icon.querySelectorAll('.cart-bubble__text-count, .cart-bubble__text, .cart-bubble span[aria-hidden="true"]');
      bubbleTexts.forEach(el => {
        el.textContent = parsedCount > 99 ? '99+' : parsedCount;
      });

      icon.style.transform = 'scale(1.15)';
      setTimeout(() => icon.style.transform = '', 200);
    });

    // 2. Update any legacy template counters
    document.querySelectorAll('[data-cart-count], .gb-bag-count').forEach(el => {
      el.textContent = count;
    });
  };

  const fetchAndUpdateCart = async () => {
    try {
      const res = await fetch(window.Shopify?.routes?.root + 'cart.js' || '/cart.js');
      const cart = await res.json();
      updateCartUINodes(cart.item_count);
    } catch (err) { console.error('Cart fetch error', err); }
  };

  // 1. Intercept fetch 
  const originalFetch = window.fetch;
  window.fetch = async function() {
    const response = await originalFetch.apply(this, arguments);
    const url = arguments[0];
    if (typeof url === 'string' && url.match(/\/cart\/(add|update|change|clear)\.js/i)) {
      response.clone().json().then(data => {
        if (typeof data.item_count !== 'undefined') updateCartUINodes(data.item_count);
        else fetchAndUpdateCart();
      }).catch(() => fetchAndUpdateCart());
    }
    return response;
  };

  // 2. Intercept XMLHttpRequest 
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url) {
    this._gbCartRequestUrl = url;
    this.addEventListener('load', () => {
      const requestUrl = this._gbCartRequestUrl;
      if (typeof requestUrl === 'string' && /\/cart\/(add|update|change|clear)\.js(?:\?|$)/i.test(requestUrl)) {
        fetchAndUpdateCart();
      }
    });
    return origOpen.apply(this, arguments);
  };

  // 3. Fallback Theme Events
  ['cart:updated', 'cart:change', 'cart:refresh', 'ajaxProduct:added'].forEach(evt => {
    document.addEventListener(evt, (e) => {
      const count = e?.detail?.cart?.item_count ?? e?.detail?.data?.itemCount;
      if (typeof count === 'number') updateCartUINodes(count);
      else fetchAndUpdateCart();
    });
  });
});